import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useApolloClient } from '@apollo/client';
import useTranslation from 'hooks/use-translation';
import sendPasswordResetEmailV2 from 'shared/graphql/password/mutations/send-password-reset-email-v2.gql';
import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_DEFAULT_MESSAGES, ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import { getLogger } from 'src/utils/datadog-logger';
import { LOG_NAMESPACES } from 'src/payments/constants';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

type UseResetPasswordResult = {
  resetPassword: ({ email }: { email: string }) => Promise<void>;
};

export const useResetPassword = (): UseResetPasswordResult => {
  const apolloClient = useApolloClient();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const showErnie = useErnie();
  const { t } = useTranslation();

  const ernieMessages = {
    success: t('resetPassword.success', 'An email has been sent with instructions'),
    failure: t('common.ernieError', '{{errorMessage}}', {
      errorMessage: ERNIE_DEFAULT_MESSAGES.ERROR,
    }),
  };
  const onFail = (): void => showErnie(ernieMessages.failure, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);

  const resetPassword = async ({ email }): Promise<void> => {
    const dispensaryId = DutchiePayEnrollment?.dispensary?.id;

    try {
      const response = await apolloClient.mutate({
        mutation: sendPasswordResetEmailV2,
        variables: { email, dispensaryId },
      });
      const { success } = response.data.sendPasswordResetEmailV2;

      if (success) {
        showErnie(ernieMessages.success, ERNIE_TYPES.SUCCESS, ERNIE_TIMEOUTS.SHORT);
        DutchiePayEnrollment.resetPasswordSucceeded();
      } else {
        throw new Error('sendPasswordResetEmailV2 mutation was not successful');
      }
    } catch (err) {
      logger.error('Password reset error', {
        error: err?.message ?? 'Unknown error',
        errorCode: err?.code ?? 'NO_CODE',
        step: 'forgotPasswordForm',
        dispensaryId: dispensaryId ?? 'unknown',
      });
      console.error(err);
      onFail();
    }
  };

  return {
    resetPassword,
  };
};
